import { Component } from '@theme/component';
import {
  fetchConfig,
  onAnimationEnd,
  preloadImage
} from '@theme/utilities';

import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent
} from '@theme/events';

import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;


/**
 * ADD TO CART COMPONENT
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      'pointerenter',
      this.#preloadImage
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }

    if (this.#cleanupTimeout) {
      clearTimeout(this.#cleanupTimeout);
    }

    this.removeEventListener(
      'pointerenter',
      this.#preloadImage
    );
  }

  /**
   * Disable add to cart button
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enable add to cart button
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handle add to cart click
   */
  handleClick(event) {
    const form = this.closest('form');

    if (!form?.checkValidity()) {
      return;
    }

    // Check quantity limit before animation
    const quantitySelector =
      /** @type {any} */
      (form.querySelector('quantity-selector-component'));

    if (quantitySelector?.canAddToCart) {
      const validation =
        quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled =
      this.dataset.addToCartAnimation === 'true';

    if (
      animationEnabled &&
      !event.target.closest('.quick-add-modal')
    ) {
      this.#animateFlyToCart();
    }
  }

  /**
   * Preload product image
   */
  #preloadImage = () => {
    const image =
      this.dataset.productVariantMedia;

    if (!image) {
      return;
    }

    preloadImage(image);
  };

  /**
   * Fly product image to cart
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;

    const cartIcon =
      document.querySelector('.header-actions__cart-icon');

    const image =
      this.dataset.productVariantMedia;

    if (
      !cartIcon ||
      !addToCartButton ||
      !image
    ) {
      return;
    }

    const flyToCartElement =
      /** @type {FlyToCart} */
      (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${image})`
    );

    flyToCartElement.source =
      addToCartButton;

    flyToCartElement.destination =
      cartIcon;

    document.body.appendChild(
      flyToCartElement
    );
  }

  /**
   * Add to cart button animation
   */
  animateAddToCart() {
    const { addToCartButton } =
      this.refs;

    if (this.#animationTimeout) {
      clearTimeout(
        this.#animationTimeout
      );
    }

    if (this.#cleanupTimeout) {
      clearTimeout(
        this.#cleanupTimeout
      );
    }

    if (
      !addToCartButton.classList.contains(
        'atc-added'
      )
    ) {
      addToCartButton.classList.add(
        'atc-added'
      );
    }

    this.#animationTimeout =
      setTimeout(() => {

        this.#cleanupTimeout =
          setTimeout(() => {

            this.refs.addToCartButton.classList.remove(
              'atc-added'
            );

          }, 10);

      }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}


if (
  !customElements.get(
    'add-to-cart-component'
  )
) {
  customElements.define(
    'add-to-cart-component',
    AddToCartComponent
  );
}


/**
 * PRODUCT FORM COMPONENT
 */
class ProductFormComponent extends Component {

  requiredRefs = [
    'variantId',
    'liveRegion'
  ];

  #abortController =
    new AbortController();

  /** @type {number | undefined} */
  #timeout;


  connectedCallback() {
    super.connectedCallback();

    const { signal } =
      this.#abortController;

    const target =
      this.closest(
        '.shopify-section, dialog, product-card'
      );

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal }
    );

    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#onCartUpdate,
      { signal }
    );
  }


  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }


  /**
   * Fetch current cart quantity for variant
   */
  async #fetchAndUpdateCartQuantity() {

    const variantIdInput =
      /** @type {HTMLInputElement | null} */
      (
        this.querySelector(
          'input[name="id"]'
        )
      );

    if (!variantIdInput?.value) {
      return 0;
    }

    try {

      const response =
        await fetch('/cart.js');

      const cart =
        await response.json();

      const cartItem =
        cart.items.find(
          /** @param {any} item */
          (item) =>
            item.variant_id.toString() ===
            variantIdInput.value.toString()
        );

      const cartQty =
        cartItem
          ? cartItem.quantity
          : 0;

      const quantitySelector =
        /** @type {any} */
        (
          this.querySelector(
            'quantity-selector-component'
          )
        );

      if (
        quantitySelector?.setCartQuantity
      ) {
        quantitySelector.setCartQuantity(
          cartQty
        );
      }

      this.#updateQuantityLabel(
        cartQty
      );

      return cartQty;

    } catch (error) {

      console.error(
        'Failed to fetch cart quantity:',
        error
      );

      return 0;
    }
  }


  /**
   * Cart update event
   */
  #onCartUpdate = async (event) => {

    // Ignore updates generated by this component
    if (
      event.detail?.sourceId === this.id ||
      event.detail?.data?.source ===
        'product-form-component'
    ) {
      return;
    }

    await this.#fetchAndUpdateCartQuantity();
  };


  /**
   * HANDLE ADD TO CART SUBMIT
   */
  handleSubmit(event) {

    const {
      addToCartTextError,
      addToCartButtonContainer
    } = this.refs;

    // Prevent normal browser form submit
    event.preventDefault();

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    // Don't continue if button is disabled
    if (
      addToCartButtonContainer
        ?.refs
        .addToCartButton
        ?.disabled
    ) {
      return;
    }


    /**
     * Get product form
     */
    const form =
      this.querySelector('form');

    if (!form) {
      throw new Error(
        'Product form element missing'
      );
    }


    /**
     * Validate quantity
     */
    const quantitySelector =
      /** @type {any} */
      (
        this.querySelector(
          'quantity-selector-component'
        )
      );

    if (
      quantitySelector?.canAddToCart
    ) {

      const validation =
        quantitySelector.canAddToCart();

      if (!validation.canAdd) {

        addToCartButtonContainer?.disable();

        const errorTemplate =
          this.dataset.quantityErrorMax ||
          '';

        const errorMessage =
          errorTemplate.replace(
            '{{ maximum }}',
            validation.maxQuantity.toString()
          );


        if (addToCartTextError) {

          addToCartTextError.classList.remove(
            'hidden'
          );

          const textNode =
            addToCartTextError.childNodes[2];

          if (textNode) {

            textNode.textContent =
              errorMessage;

          } else {

            addToCartTextError.appendChild(
              document.createTextNode(
                errorMessage
              )
            );
          }

          this.#setLiveRegionText(
            errorMessage
          );


          if (this.#timeout) {
            clearTimeout(
              this.#timeout
            );
          }


          this.#timeout =
            setTimeout(() => {

              addToCartTextError.classList.add(
                'hidden'
              );

              this.#clearLiveRegionText();

            }, ERROR_MESSAGE_DISPLAY_DURATION);
        }


        setTimeout(() => {

          addToCartButtonContainer?.enable();

        }, ERROR_BUTTON_REENABLE_DELAY);


        return;
      }
    }


    /**
     * Create form data
     */
    const formData =
      new FormData(form);


    /**
     * Get cart sections
     */
    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    const cartItemComponentsSectionIds =
      [];


    cartItemsComponents.forEach(
      (item) => {

        if (
          item instanceof HTMLElement &&
          item.dataset.sectionId
        ) {

          cartItemComponentsSectionIds.push(
            item.dataset.sectionId
          );
        }
      }
    );


    /**
     * IMPORTANT:
     * Append sections only once
     */
    if (
      cartItemComponentsSectionIds.length
    ) {

      formData.append(
        'sections',
        cartItemComponentsSectionIds.join(',')
      );
    }


    /**
     * Shopify AJAX configuration
     */
    const fetchCfg =
      fetchConfig(
        'javascript',
        {
          body: formData
        }
      );


    /**
     * ADD PRODUCT TO CART
     */
    fetch(
      Theme.routes.cart_add_url,
      {
        ...fetchCfg,

        headers: {
          ...fetchCfg.headers,

          Accept: 'text/html'
        }
      }
    )

      /**
       * Parse response
       */
      .then(
        async (response) => {

          const data =
            await response.json();

          return data;
        }
      )


      /**
       * Handle response
       */
      .then(
        (response) => {


          /**
           * =====================================
           * ERROR
           * =====================================
           */
          if (response.status) {

            this.dispatchEvent(
              new CartErrorEvent(
                form.getAttribute('id') || '',
                response.message,
                response.description,
                response.errors
              )
            );


            if (!addToCartTextError) {
              return;
            }


            addToCartTextError.classList.remove(
              'hidden'
            );


            const textNode =
              addToCartTextError.childNodes[2];


            if (textNode) {

              textNode.textContent =
                response.message;

            } else {

              addToCartTextError.appendChild(
                document.createTextNode(
                  response.message
                )
              );
            }


            this.#setLiveRegionText(
              response.message
            );


            if (this.#timeout) {
              clearTimeout(
                this.#timeout
              );
            }


            this.#timeout =
              setTimeout(() => {

                addToCartTextError.classList.add(
                  'hidden'
                );

                this.#clearLiveRegionText();

              }, ERROR_MESSAGE_DISPLAY_DURATION);


            /**
             * IMPORTANT:
             *
             * DO NOT REDIRECT TO /cart
             * WHEN THERE IS AN ERROR.
             */
            return;
          }


          /**
           * =====================================
           * SUCCESS
           * =====================================
           */

          const id =
            formData.get('id');


          if (!id) {

            throw new Error(
              'Form ID is required'
            );
          }


          /**
           * Hide old error
           */
          if (addToCartTextError) {

            addToCartTextError.classList.add(
              'hidden'
            );

            addToCartTextError.removeAttribute(
              'aria-live'
            );
          }


          /**
           * Screen reader success message
           */
          if (
            this.refs
              .addToCartButtonContainer
              ?.refs
              .addToCartButton
          ) {

            const addToCartButton =
              this.refs
                .addToCartButtonContainer
                .refs
                .addToCartButton;


            const addedTextElement =
              addToCartButton.querySelector(
                '.add-to-cart-text--added'
              );


            const addedText =
              addedTextElement
                ?.textContent
                ?.trim() ||
              Theme.translations.added;


            this.#setLiveRegionText(
              addedText
            );


            setTimeout(() => {

              this.#clearLiveRegionText();

            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }


          /**
           * Dispatch cart add event
           */
          this.dispatchEvent(
            new CartAddEvent(
              {},
              id.toString(),
              {
                source:
                  'product-form-component',

                itemCount:
                  Number(
                    formData.get('quantity')
                  ) ||
                  Number(
                    this.dataset.quantityDefault
                  ),

                productId:
                  this.dataset.productId,

                sections:
                  response.sections
              }
            )
          );


          /**
           * =====================================
           * IMPORTANT FIX
           * =====================================
           *
           * Product successfully add hone ke
           * BAAD hi cart page par redirect karein.
           *
           * response.status hone ki surat mein
           * yahan kabhi nahi aayega.
           */
          window.location.href = '/cart';

          return;
        }
      )


      /**
       * Catch errors
       */
      .catch(
        (error) => {

          console.error(
            'Add to cart error:',
            error
          );
        }
      )


      /**
       * Performance measurement
       */
      .finally(() => {

        cartPerformance.measureFromEvent(
          'add:user-action',
          event
        );
      });
  }


  /**
   * Update quantity label
   */
  #updateQuantityLabel(cartQty) {

    const quantityLabel =
      this.refs.quantityLabelCartCount;

    if (!quantityLabel) {
      return;
    }


    const inCartText =
      quantityLabel.textContent?.match(
        /\((\d+)\s+(.+)\)/
      );


    if (
      inCartText &&
      inCartText[2]
    ) {

      quantityLabel.textContent =
        `(${cartQty} ${inCartText[2]})`;
    }


    quantityLabel.classList.toggle(
      'hidden',
      cartQty === 0
    );
  }


  /**
   * Live region
   */
  #setLiveRegionText(text) {

    const liveRegion =
      this.refs.liveRegion;

    liveRegion.textContent =
      text;
  }


  #clearLiveRegionText() {

    const liveRegion =
      this.refs.liveRegion;

    liveRegion.textContent =
      '';
  }


  /**
   * Morph/update DOM element
   */
  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null
  ) {

    if (
      currentElement &&
      newElement
    ) {

      morph(
        currentElement,
        newElement
      );

    } else if (
      currentElement &&
      !newElement
    ) {

      currentElement.remove();

    } else if (
      !currentElement &&
      newElement &&
      insertReferenceElement
    ) {

      insertReferenceElement.insertAdjacentElement(
        'beforebegin',
        /** @type {Element} */
        (
          newElement.cloneNode(true)
        )
      );
    }
  }


  /**
   * VARIANT UPDATE
   */
  #onVariantUpdate =
    async (event) => {

      if (
        event.detail.data.newProduct
      ) {

        this.dataset.productId =
          event.detail.data.newProduct.id;

      } else if (
        event.detail.data.productId !==
        this.dataset.productId
      ) {

        return;
      }


      const {
        variantId,
        addToCartButtonContainer
      } = this.refs;


      const currentAddToCartButton =
        addToCartButtonContainer
          ?.refs
          .addToCartButton;


      const newAddToCartButton =
        event.detail.data.html.querySelector(
          '[ref="addToCartButton"]'
        );


      /**
       * Update variant ID
       */
      variantId.value =
        event.detail.resource?.id ??
        '';


      if (
        !currentAddToCartButton &&
        !this.refs
          .acceleratedCheckoutButtonContainer
      ) {

        return;
      }


      /**
       * Update button state
       */
      if (currentAddToCartButton) {

        if (
          event.detail.resource == null ||
          event.detail.resource.available === false
        ) {

          addToCartButtonContainer.disable();

        } else {

          addToCartButtonContainer.enable();
        }


        /**
         * Update button HTML
         */
        if (newAddToCartButton) {

          morph(
            currentAddToCartButton,
            newAddToCartButton
          );
        }
      }


      /**
       * Accelerated checkout button
       */
      if (
        this.refs
          .acceleratedCheckoutButtonContainer
      ) {

        if (
          event.detail.resource == null ||
          event.detail.resource.available === false
        ) {

          this.refs
            .acceleratedCheckoutButtonContainer
            .setAttribute(
              'hidden',
              'true'
            );

        } else {

          this.refs
            .acceleratedCheckoutButtonContainer
            .removeAttribute(
              'hidden'
            );
        }
      }


      /**
       * Variant image
       */
      if (event.detail.resource) {

        const productVariantMedia =
          event.detail.resource
            .featured_media
            ?.preview_image
            ?.src;


        if (productVariantMedia) {

          addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            productVariantMedia +
              '&width=100'
          );
        }
      }


      /**
       * Quantity selector
       */
      const quantitySelector =
        /** @type {any} */
        (
          this.querySelector(
            'quantity-selector-component'
          )
        );


      const newQuantityInput =
        /** @type {HTMLInputElement | null} */
        (
          event.detail.data.html.querySelector(
            'quantity-selector-component input[ref="quantityInput"]'
          )
        );


      if (
        quantitySelector?.updateConstraints &&
        newQuantityInput
      ) {

        quantitySelector.updateConstraints(
          newQuantityInput.min,
          newQuantityInput.max || null,
          newQuantityInput.step
        );
      }


      /**
       * Quantity rules
       */
      const quantityRules =
        this.refs.quantityRules;


      const newQuantityRules =
        event.detail.data.html.querySelector(
          '.quantity-rules'
        );


      const isQuantityRulesChanging =
        !!quantityRules !==
        !!newQuantityRules;


      if (
        isQuantityRulesChanging &&
        quantitySelector
      ) {

        const currentQuantityValue =
          quantitySelector.getValue?.();


        const currentProductFormButtons =
          this.refs.productFormButtons;


        const newProductFormButtons =
          event.detail.data.html.querySelector(
            '.product-form-buttons'
          );


        if (
          currentProductFormButtons &&
          newProductFormButtons
        ) {

          morph(
            currentProductFormButtons,
            newProductFormButtons
          );


          const newQuantitySelector =
            /** @type {any} */
            (
              this.querySelector(
                'quantity-selector-component'
              )
            );


          const newQuantityInputElement =
            /** @type {HTMLInputElement | null} */
            (
              event.detail.data.html.querySelector(
                'quantity-selector-component input[ref="quantityInput"]'
              )
            );


          if (
            newQuantitySelector?.updateConstraints &&
            newQuantityInputElement &&
            currentQuantityValue
          ) {

            newQuantitySelector.setValue(
              currentQuantityValue
            );


            newQuantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max ||
                null,
              newQuantityInputElement.step
            );
          }
        }

      } else {

        /**
         * Update quantity label
         */
        const quantityLabel =
          this.querySelector(
            '.quantity-label'
          );


        const newQuantityLabel =
          event.detail.data.html.querySelector(
            '.quantity-label'
          );


        this.#morphOrUpdateElement(
          quantityLabel,
          newQuantityLabel,
          quantitySelector
        );


        /**
         * Update quantity rules
         */
        const addToCartButton =
          this.querySelector(
            '[ref="addToCartButtonContainer"]'
          );


        this.#morphOrUpdateElement(
          quantityRules,
          newQuantityRules,
          addToCartButton
        );
      }


      /**
       * Update cart quantity
       */
      await this.#fetchAndUpdateCartQuantity();
    };


  /**
   * Disable button while variant is changing
   */
  #onVariantSelected = () => {

    this.refs
      .addToCartButtonContainer
      ?.disable();
  };
}


/**
 * Register product form component
 */
if (
  !customElements.get(
    'product-form-component'
  )
) {

  customElements.define(
    'product-form-component',
    ProductFormComponent
  );
}


/**
 * FLY TO CART COMPONENT
 */
class FlyToCart extends HTMLElement {

  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;


  connectedCallback() {

    this.#animate();
  }


  #animate() {

    const rect =
      this.getBoundingClientRect();


    const sourceRect =
      this.source.getBoundingClientRect();


    const destinationRect =
      this.destination.getBoundingClientRect();


    /**
     * Offset
     */
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2
    };


    /**
     * Start position
     */
    const startPoint = {

      x:
        sourceRect.left +
        sourceRect.width / 2 -
        offset.x,

      y:
        sourceRect.top +
        sourceRect.height / 2 -
        offset.y
    };


    /**
     * End position
     */
    const endPoint = {

      x:
        destinationRect.left +
        destinationRect.width / 2 -
        offset.x,

      y:
        destinationRect.top +
        destinationRect.height / 2 -
        offset.y
    };


    /**
     * Bezier control points
     */
    const controlPoint1 = {

      x: startPoint.x,

      y:
        startPoint.y - 200
    };


    const controlPoint2 = {

      x:
        endPoint.x - 300,

      y:
        endPoint.y - 100
    };


    /**
     * Animation
     */
    let startTime = null;

    const duration = 600;

    this.style.opacity = '1';


    const animate = (currentTime) => {

      if (!startTime) {
        startTime = currentTime;
      }


      const elapsed =
        currentTime - startTime;


      const progress =
        Math.min(
          elapsed / duration,
          1
        );


      /**
       * Calculate position
       */
      const position =
        bezierPoint(
          progress,
          startPoint,
          controlPoint1,
          controlPoint2,
          endPoint
        );


      this.style.setProperty(
        '--x',
        `${position.x}px`
      );


      this.style.setProperty(
        '--y',
        `${position.y}px`
      );


      /**
       * Scale
       */
      const scale =
        1 - progress * 0.5;


      this.style.setProperty(
        '--scale',
        `${scale}`
      );


      /**
       * Continue animation
       */
      if (progress < 1) {

        requestAnimationFrame(
          animate
        );

      } else {

        /**
         * Fade out
         */
        this.style.opacity = '0';


        onAnimationEnd(
          this,
          () => this.remove()
        );
      }
    };


    /**
     * Initial position
     */
    this.style.setProperty(
      '--x',
      `${startPoint.x}px`
    );


    this.style.setProperty(
      '--y',
      `${startPoint.y}px`
    );


    requestAnimationFrame(
      animate
    );
  }
}


/**
 * Cubic Bezier calculation
 */
function bezierPoint(
  t,
  p0,
  p1,
  p2,
  p3
) {

  const cX =
    3 * (p1.x - p0.x);


  const bX =
    3 * (p2.x - p1.x) -
    cX;


  const aX =
    p3.x -
    p0.x -
    cX -
    bX;


  const cY =
    3 * (p1.y - p0.y);


  const bY =
    3 * (p2.y - p1.y) -
    cY;


  const aY =
    p3.y -
    p0.y -
    cY -
    bY;


  const x =
    aX * Math.pow(t, 3) +
    bX * Math.pow(t, 2) +
    cX * t +
    p0.x;


  const y =
    aY * Math.pow(t, 3) +
    bY * Math.pow(t, 2) +
    cY * t +
    p0.y;


  return {
    x,
    y
  };
}


/**
 * Register fly-to-cart component
 */
if (
  !customElements.get(
    'fly-to-cart'
  )
) {

  customElements.define(
    'fly-to-cart',
    FlyToCart
  );
}